import { tallyTokens } from "../../theme.tsx";
import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import DashboardCustomizeIcon from "@mui/icons-material/DashboardCustomize";
import { useDispatch } from "react-redux";
import { useTypedSelector } from "../../hooks.tsx";
import { saveExchangeToDashboard } from "../../store/conversation.tsx";
import { useFeatureFlag } from "../../hooks/useFeatureFlag.tsx";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";

const NEW_DASHBOARD_VALUE = "__new_dashboard__";

interface SaveToDashboardPromptProps {
  exchangeId?: string;
}

const SaveToDashboardPrompt: React.FC<SaveToDashboardPromptProps> = ({
  exchangeId,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const hasDashboardsFeature = useFeatureFlag("dashboards");
  const [open, setOpen] = React.useState(false);
  const [selectedDashboard, setSelectedDashboard] = React.useState("");
  const [dashboardName, setDashboardName] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);
  const [recentlySaved, setRecentlySaved] = React.useState(false);

  const { dashboardIds, dashboardsById, dashboardStatusByExchangeId } =
    useTypedSelector((state) => state.conversationState);
  const dashboardStatus = exchangeId
    ? dashboardStatusByExchangeId[exchangeId]
    : undefined;
  const isAlreadySaved = Boolean(dashboardStatus?.saved);
  const isStatusLoading = Boolean(dashboardStatus?.loading);
  const dashboardTitleFallback = i18n._(msg`a dashboard`);

  React.useEffect(() => {
    if (dashboardIds.length === 0) {
      setSelectedDashboard(NEW_DASHBOARD_VALUE);
      return;
    }

    if (!selectedDashboard) {
      setSelectedDashboard(dashboardIds[0]);
    }
  }, [dashboardIds, selectedDashboard]);

  React.useEffect(() => {
    if (!recentlySaved) return;
    const timeout = window.setTimeout(() => setRecentlySaved(false), 2500);
    return () => window.clearTimeout(timeout);
  }, [recentlySaved]);

  if (
    !hasDashboardsFeature ||
    !exchangeId ||
    exchangeId.startsWith("-") ||
    exchangeId.startsWith("last-exchange-temp")
  ) {
    return null;
  }

  const handleClose = () => {
    setOpen(false);
    if (dashboardIds.length === 0) {
      setSelectedDashboard(NEW_DASHBOARD_VALUE);
    }
    setDashboardName("");
  };

  const handleSave = async () => {
    const creatingNewDashboard = selectedDashboard === NEW_DASHBOARD_VALUE;
    const trimmedDashboardName = dashboardName.trim();

    if (creatingNewDashboard && trimmedDashboardName === "") {
      return;
    }

    setIsSaving(true);
    try {
      await dispatch(
        saveExchangeToDashboard({
          exchangeId,
          dashboardId: creatingNewDashboard ? undefined : selectedDashboard,
          dashboardTitle: creatingNewDashboard
            ? trimmedDashboardName
            : undefined,
        }) as any,
      );
      setRecentlySaved(true);
      handleClose();
    } catch (error) {
      console.error("Failed to save dashboard tile:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const title = recentlySaved
    ? i18n._(
        msg({
          context:
            "Tooltip title for the save to dashboard button after a successful save.",
          message: "Saved to dashboard",
        }),
      )
    : isAlreadySaved
      ? i18n._(
          msg`Already saved to ${dashboardStatus?.dashboard_title || dashboardTitleFallback}`,
        )
      : isStatusLoading
        ? i18n._(
            msg({
              context:
                "Tooltip title while checking whether a response is already saved to a dashboard.",
              message: "Checking dashboard status",
            }),
          )
        : i18n._(
            msg({
              context:
                "Tooltip title for the button that saves an answer to a dashboard.",
              message: "Save to dashboard",
            }),
          );

  return (
    <React.Fragment>
      <Tooltip title={title} arrow>
        <span>
          <IconButton
            onClick={() => setOpen(true)}
            size="small"
            aria-label={i18n._(
              msg({
                context:
                  "Aria label for the button that saves an answer to a dashboard.",
                message: "Save to dashboard",
              }),
            )}
            disabled={isAlreadySaved || isStatusLoading}
          >
            {recentlySaved || isAlreadySaved ? (
              <DashboardCustomizeIcon
                fontSize="small"
                sx={{ color: theme.palette.primary.main }}
              />
            ) : (
              <DashboardCustomizeOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            backgroundColor: tallyTokens.surface,
            border: `1px solid ${tallyTokens.line}`,
            boxShadow: tallyTokens.shadow.lg,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: tallyTokens.brandInk,
            paddingBottom: 1,
          }}
        >
          {i18n._(
            msg({
              context: "Dialog title for saving a response to a dashboard.",
              message: "Save to dashboard",
            }),
          )}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ marginTop: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {i18n._(
                msg({
                  context:
                    "Dialog description for choosing an existing or new dashboard.",
                  message:
                    "Choose an existing dashboard or create a new one for this answer.",
                }),
              )}
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="dashboard-select-label">
                {i18n._(
                  msg({
                    context: "Label for dashboard selection input.",
                    message: "Dashboard",
                  }),
                )}
              </InputLabel>
              <Select
                labelId="dashboard-select-label"
                label={i18n._(
                  msg({
                    context: "Label for dashboard selection input.",
                    message: "Dashboard",
                  }),
                )}
                value={selectedDashboard}
                onChange={(event) =>
                  setSelectedDashboard(event.target.value as string)
                }
              >
                {dashboardIds.map((dashboardId) => (
                  <MenuItem key={dashboardId} value={dashboardId}>
                    {dashboardsById[dashboardId]?.title}
                  </MenuItem>
                ))}
                <MenuItem value={NEW_DASHBOARD_VALUE}>
                  {i18n._(
                    msg({
                      context: "Menu item for creating a new dashboard.",
                      message: "Create new dashboard",
                    }),
                  )}
                </MenuItem>
              </Select>
            </FormControl>
            {selectedDashboard === NEW_DASHBOARD_VALUE && (
              <TextField
                fullWidth
                label={i18n._(
                  msg({
                    context: "Label for the new dashboard name field.",
                    message: "Dashboard name",
                  }),
                )}
                value={dashboardName}
                onChange={(event) => setDashboardName(event.target.value)}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ padding: 3, paddingTop: 1 }}>
          <Button variant="outlined" onClick={handleClose}>
            {i18n._(
              msg({
                context:
                  "Button label for cancelling the save to dashboard flow.",
                message: "Cancel",
              }),
            )}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              isSaving ||
              (selectedDashboard === NEW_DASHBOARD_VALUE &&
                dashboardName.trim() === "")
            }
          >
            {i18n._(
              msg({
                context: "Button label for confirming save to dashboard.",
                message: "Save",
              }),
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default SaveToDashboardPrompt;
